import PropTypes from 'prop-types';
import React, { useCallback } from 'react';

import app from '@Stores/app';

import RuleGroup from './RuleGroup';
import { conditionOptions, defaultOptions, getCustomOptions, conditionsLabels } from './options';
import { debounce } from 'lodash';
import { verifyConflict } from './hook';

import { Button, FormGroup, Label, InputGroup, InputGroupText, SimpleAlert, Select, Badge } from 'twyne-new.ui';

export default class Rules extends React.Component {
  loadData = ({ rules }) => {
    this.setState({
      rules: rules || {
        condition: 'and',
        groups: [],
      }
    })
  }

  updateConflictMapper(conflict) {
    this.props.onConflict && this.props.onConflict(conflict ? 'conflict' : 'no-conflict')
    this.setState({ conflict })
  }

  verifyConflict = () => {
    this.props.onConflict && this.props.onConflict('checking')
    debounce(() => {
      const { rules } = this.state || {}
      const { groups } = rules || {}
      let conflict = false
      for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
        const { rules: groupRules, condition } = groups[groupIdx];
        for (let ruleIdx = 0; ruleIdx < groupRules.length; ruleIdx++) {
          const rule = groupRules[ruleIdx];
          conflict = verifyConflict({ rule, ruleIdx, groupCondition: condition, groupIdx, rules })
          if (conflict) return this.updateConflictMapper(conflict)
        }
      }
      this.updateConflictMapper(false)
    }, 300)()
  }

  componentWillMount() {
    this.loadData(this.props)
    this.loadFields()
    this.verifyConflict()
  }

  componentWillReceiveProps(props) {
    this.loadData(props)
    this.loadFields(props)
  }

  getDisabled = (rule, ruleIdx) => {
    const { origin, getDisabled } = this.props

    if (origin === 'integration' && getDisabled) {
      return getDisabled(rule, ruleIdx)
    }

    return false
  }

  disableOption = option => {
    const { origin } = this.props

    if (['form-visibility', 'form-populate'].includes(origin) && ['offer', 'form'].includes(option.type)) {
      return true
    }
    if (['integration', 'form-visibility', 'form-populate'].includes(origin) && option.key === 'slot_position') {
      return true
    }
    if (['form-visibility', 'form-populate'].includes(origin) && option.key == 'revenue') {
      return true
    }

    return false
  }

  loadFields = async props => {
    const { extraFields } = props || this.props
    let ruleOptions = defaultOptions.map(key => ({
      value: key.key,
      label: key.name,
      group: key.group,
      type: key.type,
      disabled: this.disableOption(key),
    }))

    const userProfiles = await getCustomOptions(props?.integrationType || this.props.integrationType);
    ruleOptions = ruleOptions.concat(userProfiles);

    if (extraFields) {
      ruleOptions = ruleOptions.concat(extraFields)
    }

    this.setState({ ruleOptions });
  }

  handleChange = state => {
    this.setState(state, this.verifyConflict);
    this.props.handleChange(state)
  }

  handleChangeCondition = option => {
    const { rules } = this.state;
    rules.condition = option ? option.value : null;
    this.handleChange({ rules });
  }

  handleAddRuleRow = index => {
    const { rules } = this.state;
    rules.groups[index].rules.push({});
    this.handleChange({ rules });
  }

  handleDeleteRuleRow = (index, rowIndex) => {
    const { rules } = this.state;
    rules.groups[index].rules.splice(rowIndex, 1);
    if (rules.groups[index].rules.length === 0) {
      rules.groups[index].rules.push({});
    }
    this.handleChange({ rules });
  }

  handleAddGroup = () => {
    const group = {
      condition: 'and',
      rules: [{}],
    };
    const { rules } = this.state;
    rules.groups.push(group);
    this.handleChange({ rules });
  }

  handleDeleteGroup = index => {
    const { rules } = this.state;
    rules.groups.splice(index, 1);
    this.handleChange({ rules });
  }

  handleChangeGroup = (index, group) => {
    const { rules } = this.state;
    rules.groups[index] = group;
    this.handleChange({ rules });
  }

  Alert = () => {
    const { props: { disabled }, state: { rules } } = this
    const { condition, groups } = rules

    if (!disabled && condition && !groups?.length) {
      return <SimpleAlert className="mt-2" variant="warning">
        {condition === 'none' ? <>
          No rules group, <strong>the test will pass</strong></> : <>
          No rules group, <strong>the test will not pass</strong></>}
      </SimpleAlert>
    }

    return null
  }

  RegistrationAlert = () => {
    const { props: { integrationType, origin } } = this
    const { registrationDelay = 8 } = app.site.meta || {}

    if (origin === 'integration' && integrationType?.endsWith('register')) {
      return <SimpleAlert className="mt-2" variant="warning">
        {integrationType === 'register' ? `The registration must be completed or consent to execute and will execute within ${registrationDelay} minutes.` : 'The registration must be completed or consent to execute.'}
      </SimpleAlert>
    }

    return null
  }

  Groups = () => {
    const { state: { ruleOptions, rules }, props: { origin, integrationType } } = this

    if (!ruleOptions) {
      return null
    }

    const loadRuleKeys = useCallback(async () => ruleOptions, [ruleOptions])

    return rules.groups.map((group, index, { length }) => <React.Fragment key={index}>
      <RuleGroup key={index} data={group} loadRuleKeys={loadRuleKeys}
        index={index} origin={this.props.origin} onAddRow={this.handleAddRuleRow}
        onDeleteRow={this.handleDeleteRuleRow}
        onDeleteGroup={() => this.handleDeleteGroup(index)}
        onChange={groupData => this.handleChangeGroup(index, groupData)}
        condition={this.state.rules.condition}
        disabled={this.props.disabled}
        getDisabled={(rule, ruleIdx) => this.getDisabled(rule, ruleIdx)}
        handleOpenValidatorModal={this.props.handleOpenValidatorModal}
      />
      {index < length - 1 && <Badge variant='light' className='uppercase font-semibold'>
        {conditionsLabels[this.state.rules.condition] || this.state.rules.condition}
      </Badge>}
    </React.Fragment>,
    )
  }

  RulesConflictAlert = () => {
    const { conflict } = this.state

    if (!conflict) {
      return null
    }

    return <SimpleAlert className="mt-2" variant="warning">
      <strong>Warning:</strong> The rules has a conflict <strong>and the test will not pass</strong>. Please review the rules.
    </SimpleAlert>
  }

  render() {
    return <div>
      <FormGroup>
        <Label>
          When match with
        </Label>
        <InputGroup>
          <Select
            value={this.state.rules.condition}
            clearable={false}
            options={conditionOptions}
            onChange={this.handleChangeCondition}
            required
            disabled={this.props.disabled}
          />
          <InputGroupText addonType="append" >
            Groups
          </InputGroupText>
        </InputGroup>
        <this.Alert />
        <this.RegistrationAlert />
        <this.RulesConflictAlert />
      </FormGroup>
      <this.Groups />
      <Button color="success" onClick={this.handleAddGroup} disabled={this.props.disabled}>
        Add Group
      </Button>
    </div>
  }
}

export function rulesRoot(groups = []) {
  return {
    condition: 'and',
    groups,
  }
}

export function rulesGroup(rules = []) {
  return {
    rules,
    condition: "and",
  }
}

export function rulesCondition(key, type, value) {
  return {
    key, type, value,
  }
}

Rules.propTypes = {
  extraFields: PropTypes.array,
  rules: PropTypes.any,
  loadFields: PropTypes.func,
  handleChange: PropTypes.func,
  origin: PropTypes.oneOf(['integration', 'page', 'path-page', 'profile', 'pixel', 'cms-snippet', 'form-visibility', 'form-populate', 'custom-conversion']),
  getDisabled: PropTypes.func,
  disabled: PropTypes.bool,
  handleValidatorModal: PropTypes.func,
  integrationType: PropTypes.string,
  onConflict: PropTypes.func,
};

Rules.defaultProps = {
  disabled: false,
  integrationType: null,
}
